import React, { useEffect, useMemo, useRef, useState } from "react";
import Page from "../components/Page";
import { Link } from "react-router-dom";
import { 
  IconAlertTriangle, 
  IconBox, 
  IconCarrot, 
  IconCategory2,
  IconCircleMinus, 
  IconDotsVertical, 
  IconLogs, 
  IconPencil, 
  IconPlus, 
  IconPrinter,
  IconSearch, 
  IconTrash, 
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconScale,
  IconPackage,
  IconTruck
} from "@tabler/icons-react";
import ApiClient from "../helpers/ApiClient";
import { iconStroke } from "../config/config";
import { 
  addInventoryItem, 
  addInventoryItemStockMovement, 
  deleteInventoryItem, 
  updateInventoryItem, 
  useInventoryItems, 
  addBulkStockToAllItems, 
  setBulkMinStockToAllItems,
  useInventoryCategories,
  addInventoryCategory,
  deleteInventoryCategory
} from "../controllers/inventory.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { getUserDetailsInLocalStorage } from "../helpers/UserDetails";
import toast from "react-hot-toast";
import moment from "moment";
import { mutate } from "swr";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";

export const UNIT_LABELS = {
  kg: "Kilograms",
  g: "Grams",
  l: "Liters",
  ml: "Milliliters",
  pc: "Pieces",
};

export default function InventoryPage() {
  const { t } = useTranslation();
  const {theme} = useTheme();
  const { data: storeSettings } = useStoreSettings();
  const currentUser = getUserDetailsInLocalStorage() || {};

  const { categories: inventoryCategories, APIURL: categoriesAPIURL } = useInventoryCategories();

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);
  const itemsPerPage = 20;
  const [activeActionMenuId, setActiveActionMenuId] = useState(null);

  // Clean modal state - only mount dialogs when open to avoid GPU compositor crashes
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [selectedMovementItem, setSelectedMovementItem] = useState(null);
  const [movementInputQty, setMovementInputQty] = useState("");
  const [movementTypeVal, setMovementTypeVal] = useState("IN");
  const [unitConversions, setUnitConversions] = useState([]);
  const [isConverterOpen, setIsConverterOpen] = useState(false);
  const [convPackCount, setConvPackCount] = useState('');
  const [convPackSize, setConvPackSize] = useState('');
  const [convLooseQty, setConvLooseQty] = useState('');
  const [convSelectedUnitName, setConvSelectedUnitName] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkAddModalOpen, setIsBulkAddModalOpen] = useState(false);
  const [isBulkMinStockModalOpen, setIsBulkMinStockModalOpen] = useState(false);
  const [isCategoryManageModalOpen, setIsCategoryManageModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isAddingCategoryLoading, setIsAddingCategoryLoading] = useState(false);

  const [state, setState] = useState({
    search: '',
    inventory: [],
    statusCounts: {
      all: 0,
      in: 0,
      low: 0,
      out: 0,
    },
  });

  const titleRef = useRef(null);
  const categoryRef = useRef(null);
  const quantityRef = useRef(null);
  const unitRef = useRef(null);
  const minQuantityThresholdRef = useRef(null);

  const updateTitleRef = useRef(null);
  const updateCategoryRef = useRef(null);
  const updateQuantityRef = useRef(null);
  const updateUnitRef = useRef(null);
  const updateMinQuantityThresholdRef = useRef(null);

  const [selectedUpdateItem, setSelectedUpdateItem] = useState(null);

  const [selectedDeletItem, setSelectedDeleteItem] = useState(null);

  const addStockMovementTypeRef = useRef("IN");
  const addStockMovementItemIdRef = useRef(null);
  const addStockMovementQuantityRef = useRef(null);
  const addStockMovementNoteRef = useRef(null);
  const bulkStockQuantityRef = useRef(null);
  const bulkStockNoteRef = useRef(null);
  const bulkMinStockRef = useRef(null);

  const { APIURL, data, error, isLoading, mutate: mutateInventory } = useInventoryItems({
    status: selectedStatus
  });

  useEffect(() => {
    if (data) {
      setState((prev) => ({
        ...prev,
        inventory: data.items || [],
        statusCounts: data.statusCounts || {},
      }));
    }
  }, [data]);

  useEffect(() => {
    ApiClient.get('/settings/unit-conversions')
      .then(res => setUnitConversions(Array.isArray(res.data) ? res.data : []))
      .catch(err => console.error("Failed to load unit conversions", err));
  }, []);

  const handleAddInventoryItem = async () => {
    const title = titleRef.current?.value.trim();
    const category = categoryRef.current?.value.trim() || "Category";
    const rawQuantity = (quantityRef.current?.value !== undefined && quantityRef.current?.value !== "")
      ? quantityRef.current.value
      : "0";
    const quantity = parseFloat(String(rawQuantity).replace(/,/g, ""));
    const unit = unitRef.current?.value;
    const rawMin = (minQuantityThresholdRef.current?.value !== undefined && minQuantityThresholdRef.current?.value !== "")
      ? minQuantityThresholdRef.current.value
      : "0";
    const minQuantityThreshold = parseFloat(String(rawMin).replace(/,/g, ""));

    if (!title) {
      toast.error(t('inventory.toast_item_name_required'));
      return;
    }
    if (isNaN(quantity) || quantity < 0) {
      toast.error(t('inventory.toast_quantity_positive_required') || "Quantity must be 0 or positive");
      return;
    }
    if (!unit) {
      toast.error(t('inventory.toast_unit_required'));
      return;
    }
    if (isNaN(minQuantityThreshold) || minQuantityThreshold < 0) {
      toast.error(t('inventory.toast_min_quantity_positive_required') || "Min quantity must be 0 or positive");
      return;
    }

    try {
      const res = await addInventoryItem({
        title,
        quantity,
        unit,
        min_quantity_threshold: minQuantityThreshold,
        category,
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        if (titleRef.current) titleRef.current.value = "";
        if (categoryRef.current) categoryRef.current.value = "Category";
        if (quantityRef.current) quantityRef.current.value = "";
        if (unitRef.current) unitRef.current.value = "pc";
        if (minQuantityThresholdRef.current) minQuantityThresholdRef.current.value = "";

        setIsAddModalOpen(false);
        try { document.getElementById('modal-add-inventory-item')?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleUpdateInventoryItem = async () => {
    const title = updateTitleRef.current?.value.trim();
    const category = updateCategoryRef.current?.value.trim() || "Category";
    const rawQuantity = (updateQuantityRef.current?.value !== undefined && updateQuantityRef.current?.value !== "")
      ? updateQuantityRef.current.value
      : "0";
    const quantity = parseFloat(String(rawQuantity).replace(/,/g, ""));
    const unit = updateUnitRef.current?.value;
    const rawMin = (updateMinQuantityThresholdRef.current?.value !== undefined && updateMinQuantityThresholdRef.current?.value !== "")
      ? updateMinQuantityThresholdRef.current.value
      : "0";
    const minQuantityThreshold = parseFloat(String(rawMin).replace(/,/g, ""));

    if(!selectedUpdateItem){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }
    if (!title) {
      toast.error(t('inventory.toast_item_name_required'));
      return;
    }
    if (isNaN(quantity) || quantity < 0) {
      toast.error(t('inventory.toast_quantity_positive_required') || "Quantity must be 0 or positive");
      return;
    }
    if (!unit) {
      toast.error(t('inventory.toast_unit_required'));
      return;
    }
    if (isNaN(minQuantityThreshold) || minQuantityThreshold < 0) {
      toast.error(t('inventory.toast_min_quantity_positive_required') || "Min quantity must be 0 or positive");
      return;
    }

    try {
      const res = await updateInventoryItem({
        id: selectedUpdateItem,
        title,
        unit,
        min_quantity_threshold: minQuantityThreshold,
        category,
        quantity,
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        // Optimistically update local inventory state
        setState((prev) => {
          const updatedInv = (prev.inventory || []).map((item) => {
            if (item.id == selectedUpdateItem) {
              let newStatus = 'out';
              if (quantity > minQuantityThreshold) newStatus = 'in';
              else if (quantity > 0) newStatus = 'low';
              return {
                ...item,
                title,
                unit,
                category,
                quantity,
                min_quantity_threshold: minQuantityThreshold,
                status: newStatus,
              };
            }
            return item;
          });
          return {
            ...prev,
            inventory: updatedInv,
          };
        });

        if (updateTitleRef.current) updateTitleRef.current.value = "";
        if (updateCategoryRef.current) updateCategoryRef.current.value = "Category";
        if (updateQuantityRef.current) updateQuantityRef.current.value = "";
        if (updateUnitRef.current) updateUnitRef.current.value = "pc";
        if (updateMinQuantityThresholdRef.current) updateMinQuantityThresholdRef.current.value = "";
        setSelectedUpdateItem(null);

        setIsUpdateModalOpen(false);
        try { document.getElementById('modal-update-inventory-item')?.close?.(); } catch (e) {}

        await mutateInventory();
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleDeleteInventoryItem = async () => {
    if(!selectedDeletItem){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }

    try {
      const res = await deleteInventoryItem({
        id: selectedDeletItem
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        setSelectedDeleteItem(null);

        setIsDeleteModalOpen(false);
        try { document.getElementById('modal-delete-inventory-item')?.close?.(); } catch (e) {}

        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleAddItemStockMovement = async () => {
    const movementType = addStockMovementTypeRef.current?.value || null;
    const itemId = addStockMovementItemIdRef.current?.value || null;
    const quantity = parseFloat(String(addStockMovementQuantityRef.current?.value || "0").replace(/,/g, ""));
    const note = addStockMovementNoteRef.current?.value?.trim();

    if(!itemId){
      toast.error(t('inventory.toast_invalid_request'));
      return;
    }

    if(!movementType){
      toast.error(t('inventory.toast_select_movement_type'));
      return;
    }

    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('inventory.toast_invalid_quantity'));
      return;
    }

    try {
      const res = await addInventoryItemStockMovement({
        id: itemId,
        movementType,
        quantity,
        note
      });

      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);

        // Optimistically update local inventory state
        setState((prev) => {
          const updatedInv = (prev.inventory || []).map((item) => {
            if (item.id == itemId) {
              const currentQty = parseFloat(String(item.quantity || 0).replace(/,/g, ""));
              const delta = movementType === 'IN' ? quantity : -quantity;
              const newQty = currentQty + delta;
              const minThresh = parseFloat(item.min_quantity_threshold) || 0;
              let newStatus = 'out';
              if (newQty > minThresh) newStatus = 'in';
              else if (newQty > 0) newStatus = 'low';
              return {
                ...item,
                quantity: newQty,
                status: newStatus,
              };
            }
            return item;
          });
          return {
            ...prev,
            inventory: updatedInv,
          };
        });

        if (addStockMovementTypeRef.current) addStockMovementTypeRef.current.value = 'IN';
        if (addStockMovementItemIdRef?.current) {
          addStockMovementItemIdRef.current.value = "";
        }
        if (addStockMovementQuantityRef.current) addStockMovementQuantityRef.current.value = "";
        if (addStockMovementNoteRef.current) addStockMovementNoteRef.current.value = "";

        setSelectedMovementItem(null);
        setMovementInputQty("");
        setMovementTypeVal("IN");
        setIsConverterOpen(false);
        setConvPackCount("");
        setConvPackSize("");
        setConvLooseQty("");
        setConvSelectedUnitName("");
        setIsMovementModalOpen(false);
        try { document.getElementById("modal-add-inventory-stock-movement")?.close?.(); } catch (e) {}

        await mutateInventory();
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error');
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleBulkAddStock = async () => {
    const quantity = parseFloat(String(bulkStockQuantityRef.current?.value || "0").replace(/,/g, ""));
    const note = bulkStockNoteRef.current?.value?.trim();

    if (isNaN(quantity) || quantity <= 0) {
      toast.error(t('inventory.toast_invalid_quantity') || "Invalid quantity");
      return;
    }

    try {
      const res = await addBulkStockToAllItems({ quantity, note });
      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        if (bulkStockQuantityRef.current) bulkStockQuantityRef.current.value = "";
        if (bulkStockNoteRef.current) bulkStockNoteRef.current.value = "";

        setIsBulkAddModalOpen(false);
        try { document.getElementById("modal-bulk-add-stock")?.close?.(); } catch (e) {}
        await mutateInventory();
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || t('inventory.toast_default_error') || "Error adding bulk stock";
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  const handleBulkSetMinStock = async () => {
    const min_quantity = parseFloat(String(bulkMinStockRef.current?.value || "0").replace(/,/g, ""));

    if (isNaN(min_quantity) || min_quantity < 0) {
      toast.error("Invalid minimum quantity");
      return;
    }

    try {
      const res = await setBulkMinStockToAllItems({ min_quantity });
      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message);
        if (bulkMinStockRef.current) bulkMinStockRef.current.value = "";

        setIsBulkMinStockModalOpen(false);
        try { document.getElementById("modal-bulk-set-min-stock")?.close?.(); } catch (e) {}
        await mutateInventory();
        await mutate(APIURL);
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Error setting minimum stock";
      console.error(error);
      toast.dismiss();
      toast.error(message);
    }
  };

  // Close active dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.inventory-action-dropdown')) {
        setActiveActionMenuId(null);
      }
    };
    if (activeActionMenuId !== null) {
      document.addEventListener('click', handleOutsideClick);
      return () => document.removeEventListener('click', handleOutsideClick);
    }
  }, [activeActionMenuId]);

  const handleCreateCategory = async (catName) => {
    const name = (catName || newCategoryName || "").trim();
    if (!name) {
      toast.error("Please enter a category name");
      return null;
    }
    setIsAddingCategoryLoading(true);
    try {
      const res = await addInventoryCategory({ name });
      if (res.status === 200) {
        toast.dismiss();
        toast.success(res.data.message || "Category created successfully");
        setNewCategoryName("");
        await mutate(categoriesAPIURL);
        return name;
      }
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to create category";
      toast.dismiss();
      toast.error(msg);
      return null;
    } finally {
      setIsAddingCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const res = await deleteInventoryCategory({ id: categoryId });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Category deleted");
        await mutate(categoriesAPIURL);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to delete category");
    }
  };

  // Available categories extracted from inventory categories table and current items
  const availableCategories = useMemo(() => {
    const cats = new Set();
    cats.add("Category");
    (inventoryCategories || []).forEach(c => {
      if (c.name && c.name.trim()) cats.add(c.name.trim());
    });
    (state.inventory || []).forEach(item => {
      const c = (item.category || "").trim();
      if (c) cats.add(c);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [inventoryCategories, state.inventory]);

  // Reset pagination on search, status, or category change
  useEffect(() => {
    setPage(1);
    setActiveActionMenuId(null);
  }, [state.search, selectedStatus, selectedCategory]);

  // Filter and paginate inventory items
  const filteredInventory = (state.inventory || []).filter((item) => {
    const itemCat = (item.category || "Category").trim();
    if (selectedCategory !== "all" && itemCat.toLowerCase() !== selectedCategory.toLowerCase()) {
      return false;
    }
    if (state.search) {
      const q = state.search.trim().toLowerCase();
      const matchTitle = item.title?.toLowerCase().includes(q);
      const matchCat = itemCat.toLowerCase().includes(q);
      if (!matchTitle && !matchCat) return false;
    }
    return true;
  });

  const handlePrintStockReport = () => {
    if (!filteredInventory || filteredInventory.length === 0) {
      toast.error("No items found to print report");
      return;
    }

    const storeName = storeSettings?.store_name || storeSettings?.storeName || storeSettings?.name || "Inventory Stock Report";
    const storeAddress = storeSettings?.address || "";
    const storePhone = storeSettings?.phone || "";
    const storePan = storeSettings?.pan_no || storeSettings?.panNo || "";

    const categoryText = selectedCategory === "all" ? "All Categories" : selectedCategory;
    const statusText = selectedStatus === "all" ? "All Stock Statuses" : selectedStatus.toUpperCase();
    const printedAt = moment().format("DD/MM/YYYY, h:mm A");
    const userName = currentUser?.name || currentUser?.username || "Admin";

    const totalItems = filteredInventory.length;
    const inStockCount = filteredInventory.filter(i => i.status === 'in').length;
    const lowStockCount = filteredInventory.filter(i => i.status === 'low').length;
    const outStockCount = filteredInventory.filter(i => i.status === 'out').length;

    const rowsHtml = filteredInventory.map((item, index) => {
      const qtyNum = parseFloat(item.quantity || 0);
      const isDeficit = qtyNum < 0;
      const statusLabel = isDeficit ? 'Out of Stock (Deficit)' : item.status === 'in' ? 'In Stock' : item.status === 'low' ? 'Low Stock' : 'Out of Stock';
      const statusColor = isDeficit ? '#991b1b' : item.status === 'in' ? '#166534' : item.status === 'low' ? '#854d0e' : '#991b1b';
      const statusBg = isDeficit ? '#fee2e2' : item.status === 'in' ? '#dcfce7' : item.status === 'low' ? '#fef9c3' : '#fee2e2';

      return `
        <tr style="border-bottom: 1px solid #e5e7eb;">
          <td style="padding: 7px 10px; font-size: 11px; text-align: center;">${index + 1}</td>
          <td style="padding: 7px 10px; font-size: 11px; font-weight: 600;">${item.title || '-'}</td>
          <td style="padding: 7px 10px; font-size: 11px; color: #4b5563;">${item.category || 'Category'}</td>
          <td style="padding: 7px 10px; font-size: 11px; text-align: right; font-weight: 600; color: ${isDeficit ? '#dc2626' : '#111827'};">
            ${item.quantity || 0} ${item.unit || ''}
            ${isDeficit ? '<span style="display: inline-block; font-size: 9px; padding: 1px 4px; border-radius: 3px; background: #fee2e2; color: #dc2626; margin-left: 4px;">Deficit</span>' : ''}
          </td>
          <td style="padding: 7px 10px; font-size: 11px; text-align: right; color: #6b7280;">${item.min_quantity_threshold || 0} ${item.unit || ''}</td>
          <td style="padding: 7px 10px; font-size: 11px; text-align: center;">
            <span style="display: inline-block; padding: 2px 7px; border-radius: 4px; font-weight: 600; color: ${statusColor}; background: ${statusBg};">
              ${statusLabel}
            </span>
          </td>
        </tr>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Stock Report - ${categoryText}</title>
        <style>
          @page { size: A4 portrait; margin: 12mm 15mm; }
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; padding: 0; }
          .header { text-align: center; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 12px; }
          .store-name { font-size: 19px; font-weight: bold; margin: 0; text-transform: uppercase; }
          .store-meta { font-size: 11px; color: #4b5563; margin-top: 3px; }
          .report-title { font-size: 14px; font-weight: bold; margin-top: 8px; letter-spacing: 0.5px; }
          .meta-bar { display: flex; justify-content: space-between; font-size: 11px; color: #374151; margin-bottom: 10px; padding: 6px 8px; background: #f3f4f6; border-radius: 4px; }
          .summary-cards { display: flex; gap: 10px; margin-bottom: 12px; }
          .card { flex: 1; padding: 6px 10px; border-radius: 6px; border: 1px solid #e5e7eb; font-size: 11px; }
          .card-val { font-size: 13px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin-top: 4px; }
          th { background: #1f2937; color: #ffffff; text-align: left; padding: 7px 10px; font-size: 11px; text-transform: uppercase; }
          .footer { margin-top: 18px; padding-top: 8px; border-top: 1px dashed #9ca3af; display: flex; justify-content: space-between; font-size: 10px; color: #6b7280; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="store-name">${storeName}</h1>
          ${storeAddress ? `<div class="store-meta">${storeAddress} ${storePhone ? `| Tel: ${storePhone}` : ''} ${storePan ? `| PAN/VAT: ${storePan}` : ''}</div>` : ''}
          <div class="report-title">INVENTORY STOCK REPORT</div>
        </div>

        <div class="meta-bar">
          <div><strong>Category:</strong> ${categoryText}</div>
          <div><strong>Status:</strong> ${statusText}</div>
          <div><strong>Printed:</strong> ${printedAt}</div>
        </div>

        <div class="summary-cards">
          <div class="card">Total Items: <span class="card-val">${totalItems}</span></div>
          <div class="card" style="border-left: 4px solid #16a34a;">In Stock: <span class="card-val" style="color: #16a34a;">${inStockCount}</span></div>
          <div class="card" style="border-left: 4px solid #ca8a04;">Low Stock: <span class="card-val" style="color: #ca8a04;">${lowStockCount}</span></div>
          <div class="card" style="border-left: 4px solid #dc2626;">Out of Stock: <span class="card-val" style="color: #dc2626;">${outStockCount}</span></div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">#</th>
              <th>Item Name</th>
              <th>Category</th>
              <th style="text-align: right;">Current Stock</th>
              <th style="text-align: right;">Min Alert</th>
              <th style="text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>

        <div class="footer">
          <div>Printed by: ${userName}</div>
          <div>Report generated automatically from inventory database</div>
        </div>
      </body>
      </html>
    `;

    try {
      let iframe = document.getElementById("inventory-stock-print-iframe");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "inventory-stock-print-iframe";
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "none";
        document.body.appendChild(iframe);
      }
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 350);
    } catch (e) {
      console.warn("Iframe print failed, trying window.open", e);
      const printWindow = window.open("", "_blank", "width=850,height=750");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 350);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(filteredInventory.length / itemsPerPage));
  const currentPage = Math.max(1, Math.min(page, totalPages));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInventory = filteredInventory.slice(startIndex, startIndex + itemsPerPage);

  const btnPaginationFirstPage = () => setPage(1);
  const btnPaginationPreviousPage = () => setPage(p => Math.max(1, p - 1));
  const btnPaginationNextPage = () => setPage(p => Math.min(totalPages, p + 1));
  const btnPaginationLastPage = () => setPage(totalPages);

  if (isLoading) {
    return <Page>{t('inventory.loading_text')}</Page>;
  }

  if (error) {
    return <Page>{t('inventory.error_loading_text')}</Page>;
  }

  return (
    <Page>
      <div className="flex flex-wrap gap-4 flex-col md:flex-row items-center justify-between">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <h3 className="text-2xl font-bold">{t('inventory.title')}</h3>
          <Link
            to="dashboard"
            className='rounded-lg border-none transition-colors duration-150 text-restro-text px-3 py-1.5 flex items-center gap-1 bg-restro-bg-gray hover:bg-restro-button-hover text-sm font-medium'
          >
            <IconCategory2 stroke={iconStroke} size={18} /> {t('inventory.dashboard')}
          </Link>
          <Link
            to="/dashboard/inventory/transfers"
            className='rounded-lg border-none transition-colors duration-150 text-emerald-800 dark:text-emerald-300 px-3 py-1.5 flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-sm font-medium'
          >
            <IconTruck stroke={iconStroke} size={18} /> Stock Transfers
          </Link>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="relative md:w-56">
              <IconSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                className="input input-sm input-bordered focus:outline-none focus:ring-1 focus:ring-emerald-500 pl-8 pr-2 w-full rounded-lg text-gray-700 dark:text-gray-200 h-8"
                placeholder={t('inventory.search_placeholder')}
                value={state.search}
                onChange={(e) => {
                  setState({
                    ...state,
                    search: e.target.value,
                  });
                }}
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select select-sm select-bordered h-8 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              title="Filter by Category"
            >
              <option value="all">All Categories</option>
              {availableCategories.filter(c => c !== "all").map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <button 
            type="button"
            onClick={() => setIsCategoryManageModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
            title="Manage Inventory Categories"
          >
            <IconCategory2 size={18} stroke={iconStroke}/>
            Categories
          </button>
          <Link
            to="unit-conversions"
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
            title="Manage Unit & Packaging Conversions"
          >
            <IconScale size={18} stroke={iconStroke}/>
            Unit Conversions
          </Link>
          <button 
            type="button"
            onClick={() => setIsAddModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            {t('inventory.add_stock_item')}
          </button>
          <button 
            type="button"
            onClick={() => setIsBulkAddModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            Bulk Add Stock
          </button>
          <button 
            type="button"
            onClick={() => setIsBulkMinStockModalOpen(true)}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            <IconPlus size={18} stroke={iconStroke}/>
            Bulk Set Min Stock
          </button>
          <button 
            type="button"
            onClick={handlePrintStockReport}  
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
            title="Print Stock Report for current category/status"
          >
            <IconPrinter size={18} stroke={iconStroke}/>
            Print Report
          </button>

          <Link
            to="import"
            className="text-sm rounded-lg border transition-colors duration-150 px-2.5 py-1.5 flex items-center gap-1 text-restro-text bg-restro-gray border-restro-border-green hover:bg-restro-button-hover font-medium"
          >
            {t('common.import')}
          </Link>
          <Link
            to="stock-movements"
            className='rounded-lg border-none transition-colors duration-150 text-restro-text px-3 py-1.5 flex items-center gap-1 bg-restro-bg-gray hover:bg-restro-button-hover text-sm font-medium'
          >
            <IconLogs stroke={iconStroke} size={18} /> {t('inventory.stock_movements')}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        {[
          {
            label: t('inventory.status_all'),
            icon: <IconBox stroke={1.5} className="text-gray-600" />,
            status: "all",
            count: state.inventory?.length || 0,
            border: "border-l-gray-400",
            countColor: "text-gray-600",
          },
          {
            label: t('inventory.status_in'),
            icon: <IconCarrot stroke={1.5} className="text-green-600" />,
            status: "in",
            count: state.statusCounts?.in,
            border: "border-l-restro-green",
            countColor: "text-restro-green",
          },
          {
            label: t('inventory.status_low'),
            icon: <IconAlertTriangle stroke={1.5} className="text-yellow-600" />,
            status: "low",
            count: state.statusCounts?.low,
            border: "border-l-yellow-600",
            countColor: "text-yellow-600",
          },
          {
            label: t('inventory.status_out'),
            icon: <IconCircleMinus stroke={1.5} className="text-red-600"/>,
            status: "out",
            count: state.statusCounts?.out,
            border: "border-l-red-600",
            countColor: "text-red-600",
          },
        ].map(({ label, icon, status, count, border, countColor }) => {
          const isSelected = selectedStatus === status;
          return (
            <div
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={clsx(
                "cursor-pointer flex items-center gap-4 px-4 py-4 rounded-xl border border-l-8 transition-colors duration-150",
                border,
                isSelected
                  ? theme === 'black'
                    ? "bg-restro-card-iconbg/80 border-restro-border-dark-mode"
                    : "border-restro-green-light bg-gray-50"
                  : theme === 'black'
                    ? " border-restro-border-dark-mode"
                    : "border-restro-green-light"
              )}
            >
              <div className='p-3 rounded-lg bg-restro-card-bg'>{icon}</div>
              <div>
                <div className={`text-sm font-medium ${theme === 'black' ? 'text-white' : 'text-gray-600'} `}>{label}</div>
                <div className={`text-lg font-bold ${countColor}`}>{count}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-[calc(100vh-310px)] overflow-auto w-[100%] mt-4 rounded-xl border border-gray-100 dark:border-zinc-800">
       {filteredInventory.length === 0 ? (
          <div className="flex flex-col justify-center items-center text-center h-full p-8">
            <img
              src="/assets/illustrations/orders-not-found.webp"
              alt={t('inventory.no_items_image_alt')}
              className="w-1/2 md:w-60"
            />
            <p className='text-md text-restro-text mt-4 font-medium'>{t('inventory.no_items_message')}</p>
          </div>
        ) :
        (<table className="table table-sm w-full">
          <thead className='border-b sticky top-0 z-10 bg-restro-bg-gray border-restro-border-green'>
            <tr>
              <th className="text-start p-2.5">#</th>
              <th className="text-start">{t('inventory.table_item')}</th>
              <th className="text-start">Category</th>
              <th className="text-start">{t('inventory.table_current_stock')}</th>
              <th className="text-start">{t('inventory.table_min_stock')}</th>
              <th className="text-start">{t('inventory.table_stock_status')}</th>
              <th className="text-start max-w-20">{t('inventory.table_updated_on')}</th>
              <th className="text-start w-16">{t('inventory.table_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {
              paginatedInventory.map((inventoryItem, i) => {
                const { id, title, category, quantity, unit, min_quantity_threshold, status, updated_at } = inventoryItem;
                const isMenuOpen = activeActionMenuId === id;
                return (
                  <tr key={id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                    <td>{filteredInventory.length - (startIndex + i)}</td>
                    <td>
                      <p className="text-ellipsis line-clamp-2 font-medium">{title}</p>
                    </td>
                    <td>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300">
                        {category || "Category"}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5">
                        <span className={parseFloat(quantity || 0) < 0 ? "text-red-600 font-bold" : ""}>
                          {quantity !== null && quantity !== undefined ? quantity : "-"} {unit}
                        </span>
                        {parseFloat(quantity || 0) < 0 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                            Deficit
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <p>
                        {min_quantity_threshold ? min_quantity_threshold : "-"} {unit}
                      </p>
                    </td>
                    <td>
                      <span
                        className={`text-xs rounded-lg py-1 px-2 font-semibold
                          ${status === 'in' ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300' : ''}
                          ${status === 'low' ? 'bg-yellow-100 dark:bg-yellow-600 text-yellow-600 dark:text-yellow-300' : ''}
                          ${status === 'out' ? 'bg-red-100 dark:bg-red-700 text-red-600 dark:text-red-200' : ''}
                        `}
                      >
                        {status === 'in' ? t('inventory.stock_status_in') :
                        status === 'low' ? t('inventory.stock_status_low') :
                        status === 'out' ? t('inventory.stock_status_out') : '-'}
                      </span>
                    </td>
                    <td>
                      {updated_at
                        ? moment.utc(updated_at).local().format("DD/MM/YYYY, h:mm A")
                        : "-"}
                    </td>
                    <td>
                      <div className="relative inventory-action-dropdown">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveActionMenuId(isMenuOpen ? null : id);
                          }}
                          className='text-restro-text hover:bg-restro-button-hover p-1.5 rounded-lg transition-colors duration-100'
                          title={t('inventory.table_actions')}
                        >
                          <IconDotsVertical size={18} stroke={iconStroke} />
                        </button>
                        {isMenuOpen && (
                          <div 
                            className='absolute right-0 top-full mt-1 bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-700 shadow-xl w-56 z-50 p-1.5'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setActiveActionMenuId(null);
                                setSelectedMovementItem(inventoryItem);
                                setMovementInputQty("");
                                setMovementTypeVal("IN");
                                setIsConverterOpen(false);
                                const matchedConv = (unitConversions || []).find(c => c.inventory_item_id == inventoryItem.id);
                                if (matchedConv) {
                                  setConvPackSize(matchedConv.conversion_factor || "");
                                  setConvSelectedUnitName(matchedConv.unit_name || matchedConv.unit_symbol || "Bottle");
                                } else {
                                  setConvPackSize("");
                                  setConvSelectedUnitName("Bottle");
                                }
                                setConvPackCount("");
                                setConvLooseQty("");
                                setIsMovementModalOpen(true);
                                setTimeout(() => {
                                  if (addStockMovementItemIdRef?.current) {
                                    addStockMovementItemIdRef.current.value = inventoryItem.id;
                                  }
                                  if (addStockMovementQuantityRef?.current) {
                                    addStockMovementQuantityRef.current.value = "";
                                  }
                                }, 50);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                              <IconPlus size={16} stroke={iconStroke} /> {t('inventory.dropdown_add_stock_movement')}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setSelectedUpdateItem(inventoryItem.id);
                                setActiveActionMenuId(null);
                                setIsUpdateModalOpen(true);
                                setTimeout(() => {
                                  if (updateTitleRef.current) updateTitleRef.current.value = inventoryItem.title || "";
                                  if (updateCategoryRef.current) updateCategoryRef.current.value = inventoryItem.category || "Category";
                                  if (updateQuantityRef.current) updateQuantityRef.current.value = (inventoryItem.quantity !== undefined && inventoryItem.quantity !== null) ? inventoryItem.quantity : "0";
                                  if (updateUnitRef.current) updateUnitRef.current.value = inventoryItem.unit || "pc";
                                  if (updateMinQuantityThresholdRef.current) updateMinQuantityThresholdRef.current.value = (inventoryItem.min_quantity_threshold !== undefined && inventoryItem.min_quantity_threshold !== null) ? inventoryItem.min_quantity_threshold : "0";
                                }, 50);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                              <IconPencil size={16} stroke={iconStroke} /> {t('inventory.dropdown_update_item')}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setSelectedDeleteItem(inventoryItem?.id || null);
                                setActiveActionMenuId(null);
                                setIsDeleteModalOpen(true);
                              }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            >
                              <IconTrash size={16} stroke={iconStroke} /> {t('inventory.dropdown_delete_item')}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>)}
      </div>

      {/* Pagination Controls */}
      {filteredInventory.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4 px-2">
          <span className="text-xs text-gray-500 font-medium">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredInventory.length)} of {filteredInventory.length} items
          </span>
          <div className="join">
            <button
              type="button"
              onClick={btnPaginationFirstPage}
              disabled={currentPage === 1}
              className="join-item btn btn-xs px-2.5 rounded-l-lg border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="First Page"
            >
              <IconChevronsLeft size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              onClick={btnPaginationPreviousPage}
              disabled={currentPage === 1}
              className="join-item btn btn-xs px-2.5 border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Previous Page"
            >
              <IconChevronLeft size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              className="join-item btn btn-xs px-4 border border-gray-300 dark:border-zinc-700 font-bold pointer-events-none"
            >
              Page {currentPage} of {totalPages}
            </button>
            <button
              type="button"
              onClick={btnPaginationNextPage}
              disabled={currentPage === totalPages}
              className="join-item btn btn-xs px-2.5 border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Next Page"
            >
              <IconChevronRight size={16} stroke={iconStroke} />
            </button>
            <button
              type="button"
              onClick={btnPaginationLastPage}
              disabled={currentPage === totalPages}
              className="join-item btn btn-xs px-2.5 rounded-r-lg border border-gray-300 dark:border-zinc-700 disabled:opacity-40"
              title="Last Page"
            >
              <IconChevronsRight size={16} stroke={iconStroke} />
            </button>
          </div>
        </div>
      )}

      {/* dialog: add stock items */}    
      {isAddModalOpen && (
        <dialog id="modal-add-inventory-item" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_add_title')}</h3>
              <button 
                type="button"
                className='text-red-500 p-2 rounded-full bg-restro-bg-gray hover:bg-restro-button-hover'  
                onClick={() => setIsAddModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {/* Title */}
            <div className="mt-4">
              <label htmlFor="item_title" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_name_label')}</label>
              <input ref={titleRef} type="text"  className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_name_placeholder')} />
            </div>

            {/* Category */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="item_category" className="block text-gray-500 text-sm">Category</label>
                <button
                  type="button"
                  onClick={() => setIsCategoryManageModalOpen(true)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium underline"
                >
                  + Add / Manage Categories
                </button>
              </div>
              <select 
                ref={categoryRef} 
                defaultValue="Category" 
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green bg-white dark:bg-zinc-800'
              >
                {availableCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex items-end gap-4">
              <div className="flex-1">
                <label htmlFor="item_unit" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_unit_label')}</label>
                <select ref={unitRef} defaultValue="pc" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'>
                  <option value="pc">{t('inventory.form_item_unit_pc')}</option>
                  <option value="kg">{t('inventory.form_item_unit_kg')}</option>
                  <option value="g">{t('inventory.form_item_unit_g')}</option>
                  <option value="l">{t('inventory.form_item_unit_l')}</option>
                  <option value="ml">{t('inventory.form_item_unit_ml')}</option>
                </select>
              </div>
            </div>

            {/* Current Qty and Min Qty */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label htmlFor="item_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_qty_label')}</label>
                <input ref={quantityRef} type="number" step="any" min="0" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_qty_placeholder')} />
              </div>
              <div className="flex-1">
                <label htmlFor="low_stock_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_item_min_qty_label')}</label>
                <input ref={minQuantityThresholdRef} type="number" step="any" min="0" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_min_qty_placeholder')} />
              </div>
            </div>

            {/* Actions */}
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleAddInventoryItem}
                className='flex justify-center w-full rounded-xl transition-colors duration-150 px-4 py-3 text-white border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: update item */}
      {isUpdateModalOpen && (
        <dialog id="modal-update-inventory-item" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_update_title')}</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full bg-restro-bg-gray hover:bg-restro-button-hover' 
                onClick={() => {
                  setIsUpdateModalOpen(false);
                  setSelectedUpdateItem(null);
                }}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {/* Title */}
            <div className="mt-4">
              <label htmlFor="item_title" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_item_name_label')}</label>
              <input ref={updateTitleRef} type="text" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_item_name_placeholder')} />
            </div>

            {/* Category */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="update_item_category" className="block text-gray-500 text-sm">Category</label>
                <button
                  type="button"
                  onClick={() => setIsCategoryManageModalOpen(true)}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium underline"
                >
                  + Add / Manage Categories
                </button>
              </div>
              <select 
                ref={updateCategoryRef} 
                defaultValue="Category" 
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green bg-white dark:bg-zinc-800'
              >
                {availableCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="mt-4 flex items-end gap-4">
              <div className="flex-1">
                <label htmlFor="item_unit" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_unit_label')}</label>
                <select ref={updateUnitRef} disabled className='disabled:cursor-not-allowed text-sm w-full border rounded-lg px-4 py-2 border-restro-border-green dark:bg-black focus:outline-restro-border-green' >
                  <option value="pc">{t('inventory.form_item_unit_pc')}</option>
                  <option value="kg">{t('inventory.form_item_unit_kg')}</option>
                  <option value="g">{t('inventory.form_item_unit_g')}</option>
                  <option value="l">{t('inventory.form_item_unit_l')}</option>
                  <option value="ml">{t('inventory.form_item_unit_ml')}</option>
                </select>
              </div>
            </div>

            {/* Current Qty and Min Qty */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex-1">
                <label htmlFor="item_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_qty_label')}</label>
                <input ref={updateQuantityRef} type="number" step="any" min="0" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_update_qty_placeholder')} />
                <span className="text-[10px] text-gray-400 mt-0.5 block">Directly adjust item stock quantity</span>
              </div>
              <div className="flex-1">
                <label htmlFor="low_stock_qty" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_update_min_qty_label')}</label>
                <input ref={updateMinQuantityThresholdRef} type="number" step="any" min="0" className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' placeholder={t('inventory.form_update_min_qty_placeholder')} />
              </div>
            </div>

            {/* Actions */}
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleUpdateInventoryItem}
                className='flex w-full justify-center rounded-xl transition-colors duration-150 px-4 py-3 text-white border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: add stock movement */}
      {isMovementModalOpen && (
        <dialog id="modal-add-inventory-stock-movement" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">{t('inventory.modal_add_stock_movement_title')}</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => {
                  setSelectedMovementItem(null);
                  setMovementInputQty("");
                  setMovementTypeVal("IN");
                  setIsConverterOpen(false);
                  setConvPackCount("");
                  setConvPackSize("");
                  setConvLooseQty("");
                  setConvSelectedUnitName("");
                  setIsMovementModalOpen(false);
                }}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {selectedMovementItem && (
              <div className="mt-4 p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/60">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-restro-text">{selectedMovementItem.title}</span>
                  <span className={`font-bold ${parseFloat(selectedMovementItem.quantity || 0) < 0 ? 'text-red-600' : 'text-restro-green'}`}>
                    Current Stock: {selectedMovementItem.quantity || 0} {selectedMovementItem.unit}
                    {parseFloat(selectedMovementItem.quantity || 0) < 0 && " (Deficit)"}
                  </span>
                </div>
                {parseFloat(selectedMovementItem.quantity || 0) < 0 && (
                  <div className="mt-2 text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 p-2 rounded-lg border border-amber-200 dark:border-amber-800/50">
                    <p className="font-semibold">⚠️ Negative Stock (Deficit):</p>
                    <p className="mt-0.5">Adding stock (IN) will automatically calculate and offset this negative deficit.</p>
                    {movementTypeVal === 'IN' && !isNaN(parseFloat(movementInputQty)) && parseFloat(movementInputQty) > 0 && (
                      <p className="mt-1.5 font-bold text-emerald-700 dark:text-emerald-400">
                        Preview: {parseFloat(selectedMovementItem.quantity || 0)} + {parseFloat(movementInputQty)} = {(parseFloat(selectedMovementItem.quantity || 0) + parseFloat(movementInputQty)).toFixed(2)} {selectedMovementItem.unit} new stock
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <label htmlFor="movement_type" className="mb-1 block text-gray-500 text-sm">
                {t('inventory.modal_add_stock_movement_type')}
              </label>
              <select
                ref={addStockMovementTypeRef}
                name="movement_type"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green'
                value={movementTypeVal}
                onChange={(e) => setMovementTypeVal(e.target.value)}
              >
                <option value="IN">{t('inventory.movement_type_in')}</option>
                <option value="OUT">{t('inventory.movement_type_out')}</option>
                <option value="WASTAGE">{t('inventory.movement_type_wastage')}</option>
              </select>
            </div>

            {/* Packaging & Unit Calculator Accordion */}
            {selectedMovementItem && (
              <div className="mt-4 border border-indigo-200 dark:border-indigo-900/60 rounded-xl overflow-hidden bg-indigo-50/30 dark:bg-indigo-950/20">
                <button
                  type="button"
                  onClick={() => setIsConverterOpen(!isConverterOpen)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-semibold text-indigo-800 dark:text-indigo-300 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <IconScale size={16} stroke={iconStroke} className="text-indigo-600 dark:text-indigo-400" />
                    <span>📦 Packaging & Unit Calculator (e.g. Bottles + Loose ml)</span>
                  </div>
                  <span className="text-[11px] font-medium underline">
                    {isConverterOpen ? "Hide Calculator" : "Open Calculator"}
                  </span>
                </button>

                {isConverterOpen && (
                  <div className="p-3.5 border-t border-indigo-200 dark:border-indigo-900/60 bg-white dark:bg-zinc-900/95 text-xs space-y-3">
                    {/* Quick presets for this item if any configured */}
                    {(() => {
                      const targetUnit = selectedMovementItem?.unit || 'unit';
                      const targetItemId = selectedMovementItem?.id;
                      const itemConvs = targetItemId ? (unitConversions || []).filter(c => c.inventory_item_id == targetItemId) : [];
                      if (itemConvs.length === 0) return null;
                      return (
                        <div>
                          <label className="text-gray-500 dark:text-gray-400 block mb-1 font-medium">
                            Configured Packaging Units:
                          </label>
                          <div className="flex flex-wrap gap-1.5">
                            {itemConvs.map(c => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                  setConvPackSize(c.conversion_factor || '');
                                  setConvSelectedUnitName(c.unit_name || c.unit_symbol || 'Bottle');
                                }}
                                className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${
                                  convPackSize == c.conversion_factor
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                    : 'bg-gray-50 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-zinc-700 hover:bg-gray-100'
                                }`}
                              >
                                {c.unit_name} (1 {c.unit_symbol || c.unit_name} = {c.conversion_factor} {targetUnit})
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {(() => {
                      const targetUnit = selectedMovementItem?.unit || 'unit';
                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                          <div>
                            <label className="text-gray-600 dark:text-gray-400 block mb-1 font-medium">
                              Full Units / Packs:
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={convPackCount}
                              onChange={(e) => setConvPackCount(e.target.value)}
                              placeholder="e.g. 2"
                              className="w-full text-xs rounded-lg px-2.5 py-1.5 border border-gray-300 dark:border-zinc-700 dark:bg-black focus:outline-indigo-500"
                            />
                            <span className="text-[10px] text-gray-400 mt-0.5 block">
                              e.g. 2 Bottles / Cases
                            </span>
                          </div>

                          <div>
                            <label className="text-gray-600 dark:text-gray-400 block mb-1 font-medium">
                              Pack Size ({targetUnit} each):
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={convPackSize}
                              onChange={(e) => setConvPackSize(e.target.value)}
                              placeholder="e.g. 750"
                              className="w-full text-xs rounded-lg px-2.5 py-1.5 border border-gray-300 dark:border-zinc-700 dark:bg-black focus:outline-indigo-500"
                            />
                            <span className="text-[10px] text-gray-400 mt-0.5 block">
                              e.g. 750 {targetUnit} per bottle
                            </span>
                          </div>

                          <div>
                            <label className="text-gray-600 dark:text-gray-400 block mb-1 font-medium">
                              Loose / Extra ({targetUnit}):
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={convLooseQty}
                              onChange={(e) => setConvLooseQty(e.target.value)}
                              placeholder="e.g. 600"
                              className="w-full text-xs rounded-lg px-2.5 py-1.5 border border-gray-300 dark:border-zinc-700 dark:bg-black focus:outline-indigo-500"
                            />
                            <span className="text-[10px] text-gray-400 mt-0.5 block">
                              e.g. 600 {targetUnit} loose/open
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Calculated Formula & Total */}
                    {(() => {
                      const targetUnit = selectedMovementItem?.unit || 'unit';
                      const pCount = Number.isFinite(parseFloat(convPackCount)) ? parseFloat(convPackCount) : 0;
                      const pSize = Number.isFinite(parseFloat(convPackSize)) ? parseFloat(convPackSize) : 0;
                      const lQty = Number.isFinite(parseFloat(convLooseQty)) ? parseFloat(convLooseQty) : 0;
                      const calcTotal = (pCount * pSize) + lQty;
                      const total = Number.isFinite(calcTotal) ? calcTotal : 0;

                      return (
                        <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <div className="font-semibold text-indigo-950 dark:text-indigo-200">
                              Total Base Stock: <span className="text-base text-indigo-600 dark:text-indigo-400 font-black">{total.toLocaleString()}</span> {targetUnit}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              Formula: ({pCount} × {pSize} {targetUnit}) + {lQty} {targetUnit} = {total} {targetUnit}
                            </div>
                          </div>

                          {total > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setMovementInputQty(String(total));
                                if (addStockMovementQuantityRef.current) {
                                  addStockMovementQuantityRef.current.value = String(total);
                                }
                                const unitLabel = convSelectedUnitName || "Bottle/Pack";
                                const noteStr = `Refill via packaging: ${pCount} ${unitLabel} (${pSize} ${targetUnit} each) + ${lQty} ${targetUnit} loose = ${total} ${targetUnit}`;
                                if (addStockMovementNoteRef.current) {
                                  addStockMovementNoteRef.current.value = noteStr;
                                }
                                toast.success(`Applied ${total} ${targetUnit} to quantity!`);
                              }}
                              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold text-xs shadow-sm transition-colors whitespace-nowrap self-end sm:self-center"
                            >
                              ✓ Apply to Quantity
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <input type="hidden" ref={addStockMovementItemIdRef} />
              <label htmlFor="add_quantity" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_add_stock_qty_label')}</label>
              <input
                ref={addStockMovementQuantityRef}
                type="number"
                name="add_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder={t('inventory.form_add_stock_qty_placeholder')}
                value={movementInputQty}
                onChange={(e) => setMovementInputQty(e.target.value)}
              />
            </div>

            <div className="mt-4">
              <label htmlFor="add_notes" className="mb-1 block text-gray-500 text-sm">{t('inventory.form_add_stock_notes_label')}</label>
              <textarea
                ref={addStockMovementNoteRef}
                name="add_notes"
                rows={4}
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green resize-none'
                placeholder={t('inventory.form_add_stock_notes_placeholder')}
              />
            </div>

            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleAddItemStockMovement}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                {t('inventory.form_save_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: delete inventory item */}
      {isDeleteModalOpen && (
        <dialog id="modal-delete-inventory-item" open={true} className="modal modal-open">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <h3 className="font-bold text-lg">{t('inventory.modal_delete_title')}</h3>
            <p className="py-4 text-gray-600 dark:text-gray-300">
              {t('inventory.modal_delete_confirm')}
            </p>
            <div className="modal-action">
              <button 
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setSelectedDeleteItem(null);
                }}
                className='btn btn-sm px-4 rounded-xl border border-gray-300 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-200'
              >
                {t('inventory.modal_delete_cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteInventoryItem}
                className="ml-2 btn btn-sm rounded-xl hover:bg-red-700 bg-red-600 text-white font-bold"
              >
                {t('inventory.modal_delete_confirm_button')}
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: bulk add stock */}
      {isBulkAddModalOpen && (
        <dialog id="modal-bulk-add-stock" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">Add Stock to ALL Inventory Items</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => setIsBulkAddModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">This will instantly increase the stock quantity of every single inventory item in the system.</p>
              <label htmlFor="bulk_add_quantity" className="mb-1 block text-gray-500 text-sm">Quantity to add to ALL items:</label>
              <input
                ref={bulkStockQuantityRef}
                type="number"
                name="bulk_add_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder="e.g. 1000"
              />
            </div>
            <div className="mt-4">
              <label htmlFor="bulk_add_notes" className="mb-1 block text-gray-500 text-sm">Notes (Optional):</label>
              <textarea
                ref={bulkStockNoteRef}
                name="bulk_add_notes"
                rows={2}
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green resize-none'
                placeholder="e.g. Bulk Advance Upload"
              />
            </div>
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleBulkAddStock}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                Increase Stock of All Items
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: bulk set min stock */}
      {isBulkMinStockModalOpen && (
        <dialog id="modal-bulk-set-min-stock" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900'>
            <div className='flex justify-between items-center'>
              <h3 className="font-bold text-lg">Set Min. Stock for ALL Items</h3>
              <button 
                type="button"
                className='text-red-600 p-2 rounded-full hover:bg-restro-button-hover' 
                onClick={() => setIsBulkMinStockModalOpen(false)}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-4">This will set the Minimum Quantity Threshold for every single inventory item in the system. Items with quantity below this threshold will be marked as "Low Stock".</p>
              <label htmlFor="bulk_min_quantity" className="mb-1 block text-gray-500 text-sm">Minimum Stock Quantity:</label>
              <input
                ref={bulkMinStockRef}
                type="number"
                name="bulk_min_quantity"
                className='text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green' 
                placeholder="e.g. 5"
              />
            </div>
            <div className="modal-action w-full mt-6">
              <button
                type="button"
                onClick={handleBulkSetMinStock}
                className='rounded-xl transition-colors duration-150 px-4 py-3 text-white w-full border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover font-bold'
              >
                Set Minimum Stock for All
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* dialog: manage inventory categories */}
      {isCategoryManageModalOpen && (
        <dialog id="modal-manage-inventory-categories" open={true} className="modal modal-open modal-bottom sm:modal-middle">
          <div className='modal-box border border-restro-border-green dark:rounded-2xl bg-white dark:bg-zinc-900 max-w-md'>
            <div className='flex justify-between items-center pb-3 border-b border-gray-100 dark:border-zinc-800'>
              <div className="flex items-center gap-2">
                <IconCategory2 size={20} stroke={iconStroke} className="text-emerald-600" />
                <h3 className="font-bold text-lg">Inventory Categories</h3>
              </div>
              <button 
                type="button"
                className='text-gray-500 hover:text-red-500 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors' 
                onClick={() => {
                  setIsCategoryManageModalOpen(false);
                  setNewCategoryName("");
                }}
              >
                <IconX size={18} stroke={iconStroke}/>
              </button>
            </div>

            {/* Create category input */}
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateCategory();
              }}
              className="mt-4"
            >
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                Create New Category
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g. Vegetables, Meat, Dairy, Bakery"
                  className="text-sm flex-1 rounded-lg px-3 py-2 border border-restro-border-green dark:bg-black focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <button
                  type="submit"
                  disabled={isAddingCategoryLoading || !newCategoryName.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <IconPlus size={16} stroke={iconStroke} />
                  Add
                </button>
              </div>
            </form>

            {/* Categories List */}
            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                Existing Categories ({availableCategories.length})
              </label>
              <div className="max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-zinc-800 rounded-lg border border-gray-200 dark:border-zinc-800">
                {availableCategories.map((catName) => {
                  const dbCat = (inventoryCategories || []).find(c => c.name?.toLowerCase() === catName.toLowerCase());
                  const isDefault = catName.toLowerCase() === "category";

                  return (
                    <div key={catName} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-zinc-800/50">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {catName}
                        {isDefault && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-zinc-800 text-gray-500 font-normal">
                            Default
                          </span>
                        )}
                      </span>
                      {dbCat && !isDefault && (
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(dbCat.id)}
                          className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          title={`Delete ${catName}`}
                        >
                          <IconTrash size={15} stroke={iconStroke} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-action mt-5">
              <button
                type="button"
                onClick={() => {
                  setIsCategoryManageModalOpen(false);
                  setNewCategoryName("");
                }}
                className="btn btn-sm w-full rounded-lg border border-gray-300 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-200 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

    </Page>
  );
}
